import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './authContext';
import { getUserContext } from '../network/apis/user.api';
import { getOrganizationMembers } from '../network/apis/organization.api';

const OrganizationContext = createContext();

export const useOrganization = () => useContext(OrganizationContext);

/**
 * OrganizationContext Provider
 *
 * Manages user's organization context including:
 * - Current organization selection
 * - User's role in the organization
 * - Organization subscription limits
 * - Organization switching functionality
 */
export const OrganizationProvider = ({ children }) => {
    const { authenticated, token } = useAuth();

    // User context data from API
    const [userContext, setUserContext] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Current organization selection (null for individual users)
    const [currentOrganization, setCurrentOrganization] = useState(null);

    // Organization members (for current organization)
    const [organizationMembers, setOrganizationMembers] = useState([]);

    /**
     * Fetch user context from backend
     * Returns user profile, individual subscription, and organizations
     */
    const fetchUserContext = async () => {
        try {
            setLoading(true);
            setError(null);
            const context = await getUserContext(token);
            setUserContext(context);

            // Check if user explicitly chose individual mode
            const savedViewMode = localStorage.getItem('viewMode');

            if (savedViewMode === 'individual') {
                // User explicitly switched to individual mode (regardless of subscription status)
                setCurrentOrganization(null);
                localStorage.removeItem('currentOrganizationUuid');
            } else if (context.organizations && context.organizations.length === 1) {
                // Auto-select organization if user has exactly one
                setCurrentOrganization(context.organizations[0]);
                localStorage.setItem('currentOrganizationUuid', context.organizations[0].organizationUuid);
                localStorage.setItem('viewMode', 'organization');
            } else if (context.organizations && context.organizations.length > 1) {
                // Try to restore previously selected organization from localStorage
                const savedOrgUuid = localStorage.getItem('currentOrganizationUuid');
                if (savedOrgUuid) {
                    const savedOrg = context.organizations.find(
                        org => org.organizationUuid === savedOrgUuid
                    );
                    if (savedOrg) {
                        setCurrentOrganization(savedOrg);
                        localStorage.setItem('viewMode', 'organization');
                    } else {
                        // Default to first organization if saved one not found
                        setCurrentOrganization(context.organizations[0]);
                        localStorage.setItem('currentOrganizationUuid', context.organizations[0].organizationUuid);
                        localStorage.setItem('viewMode', 'organization');
                    }
                } else {
                    // No saved preference, default to first organization
                    setCurrentOrganization(context.organizations[0]);
                    localStorage.setItem('currentOrganizationUuid', context.organizations[0].organizationUuid);
                    localStorage.setItem('viewMode', 'organization');
                }
            } else {
                // Individual user with no organizations
                setCurrentOrganization(null);
                localStorage.removeItem('currentOrganizationUuid');
                localStorage.setItem('viewMode', 'individual');
            }

        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Switch to a different organization
     * @param {string} organizationUuid - UUID of the organization to switch to
     */
    const switchOrganization = (organizationUuid) => {
        if (!userContext?.organizations) return;

        const org = userContext.organizations.find(
            o => o.organizationUuid === organizationUuid
        );

        if (org) {
            setCurrentOrganization(org);
            localStorage.setItem('currentOrganizationUuid', organizationUuid);
            localStorage.setItem('viewMode', 'organization');
        } else {
        }
    };

    /**
     * Switch to individual mode
     * Allows users with both individual subscription and organization membership
     * to access their individual features
     */
    const switchToIndividualMode = () => {
        setCurrentOrganization(null);
        localStorage.removeItem('currentOrganizationUuid');
        localStorage.setItem('viewMode', 'individual');
    };

    /**
     * Fetch organization members
     */
    const fetchOrganizationMembers = async (orgUuid) => {
        try {
            const data = await getOrganizationMembers(token, orgUuid);
            // Filter to only active members
            const activeMembers = (data.members || [])
                .filter(member => member.status === 'active')
                .map(member => ({
                    id: member.userId,
                    firstName: member.user?.firstName || '',
                    lastName: member.user?.lastName || '',
                    email: member.user?.email || member.inviteeEmail || '',
                    role: member.role
                }));
            setOrganizationMembers(activeMembers);
        } catch (err) {
            setOrganizationMembers([]);
        }
    };

    /**
     * Refresh user context (useful after creating/joining an organization)
     */
    const refreshContext = () => {
        if (authenticated && token) {
            fetchUserContext();
        }
    };

    // Fetch user context on mount and when authentication changes
    useEffect(() => {
        if (authenticated && token) {
            fetchUserContext();
        } else {
            setUserContext(null);
            setCurrentOrganization(null);
            setOrganizationMembers([]);
            setLoading(false);
        }
    }, [authenticated, token]);

    // Fetch organization members when organization changes
    useEffect(() => {
        if (currentOrganization?.organizationUuid && token) {
            fetchOrganizationMembers(currentOrganization.organizationUuid);
        } else {
            setOrganizationMembers([]);
        }
    }, [currentOrganization, token]);

    // Computed values
    const isOrganizationUser = userContext?.organizations && userContext.organizations.length > 0;
    const isIndividualUser = !isOrganizationUser;
    const hasMultipleOrganizations = userContext?.organizations && userContext.organizations.length > 1;

    // Multi-tenant support: user can switch between individual and organization modes
    const isInOrganizationMode = currentOrganization !== null;
    const hasIndividualSubscription = userContext?.individualSubscription !== null;
    const hasEnterpriseSubscription = userContext?.individualSubscription?.isEnterprise === true;

    // Current user's role in the selected organization
    const userRole = currentOrganization?.userRole || null;

    // Current subscription limits (organization or individual)
    const subscriptionLimits = currentOrganization?.subscription?.limits
        || userContext?.individualSubscription?.limits
        || { maxDrones: 0, maxBatteries: 0, maxPayloads: 0 };

    // Subscription status
    const subscriptionStatus = currentOrganization?.subscription?.status
        || userContext?.individualSubscription?.status
        || null;

    const value = {
        // User context data
        userContext,
        loading,
        error,

        // Organization management
        currentOrganization,
        organizations: userContext?.organizations || [],
        organizationMembers,
        switchOrganization,
        switchToIndividualMode,
        refreshContext,

        // User type flags
        isOrganizationUser,
        isIndividualUser,
        hasMultipleOrganizations,
        isInOrganizationMode,
        hasIndividualSubscription,
        hasEnterpriseSubscription,

        // Current context details
        userRole,
        subscriptionLimits,
        subscriptionStatus,

        // Individual subscription (for individual users)
        individualSubscription: userContext?.individualSubscription || null,
    };

    return (
        <OrganizationContext.Provider value={value}>
            {children}
        </OrganizationContext.Provider>
    );
};
